/**
 * Workflow templates — the gallery, and switching one on for the selected org.
 *
 * PLATFORM-ADMIN ONLY (plan `credit-note-workflow-template` D4): switching a template
 * on applies DDL to the customer's database, and the dp routes refuse anyone else
 * (`require_platform_admin`). Hiding the page is the UX half; the backend gate is the
 * enforcement. The org switched on is the one selected in the portal — a platform
 * admin switches org first.
 *
 * The org must be registered as a managed tenant (dp `[dp_workflows].[ManagedTenant]`,
 * plan `xero-templates-form-and-shared-control-schema` D4/D11). The registration card
 * reads that row and, when there is none, registers the org from this page; Switch on
 * stays disabled until it is registered. Every template for the org lands in the one
 * control schema the row names — the form never asks for it.
 *
 * An org may have more than one integration (source→target, plan
 * per-integration-control-schemas D1), each with its own control schema and canonical
 * folder. With one, the page renders exactly as it did before integrations existed
 * (D17). With more, the registration card lists them, and the switch-on form asks which
 * integration the instance goes into. A non-default integration's instance is keyed
 * `<workflow key>--<integration key>`, and its Start choices are limited to workflows of
 * the same integration (D7). The form sends `integration_key` only in that case.
 *
 * The form asks for no schema: dp derives every one (plan
 * xero-template-form-derive-schema-and-tax-picker D1/D2) — the ledger mirror is the
 * mirror connector's own `target.schema`, and the source view is listed from the org's
 * control schema on the mirror connector's database. Connectors and the source view are
 * pick-lists of the selected org's own connectors and databases; choosing a new connector
 * clears the view picked under it. Each fee-mapping row picks its Xero contact and GL
 * account, and the "Xero tax type" field its tax rate (`Name (TaxType)`, storing the
 * TaxType), from the mirror connector the config field names (`depends_on`); a new mirror
 * connector clears those picks and puts the tax type back to the template default.
 *
 * Switching on creates the workflow DISABLED. The success panel links to the
 * definitions list, where enabling it is a separate, deliberate step.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  LayoutTemplate,
  Loader2,
  Plus,
  Power,
  ShieldOff,
  Trash2,
  TriangleAlert,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { PickerField } from "@/components/portal/workflow-flow-v2/inspector/fields";
import { ConnectorObjectSelect } from "@/components/portal/workflows/ConnectorObjectSelect";
import { TemplateHowItWorks } from "@/components/portal/workflows/TemplateHowItWorks";
import { TemplatePrice } from "@/components/portal/workflows/TemplatePrice";
import { WorkflowKeySelect } from "@/components/portal/workflows/WorkflowKeySelect";
import { XeroMirrorCombobox } from "@/components/portal/workflows/XeroMirrorCombobox";
import { xeroMirrorLabel } from "@/components/portal/workflows/xeroMirrorLabel";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { PortalApiError } from "@/lib/portalApi";
import {
  workflowsApi,
  type CurrentTemplateInstance,
  type DefinitionKeyResponse,
  type InstantiateWorkflowTemplateResponse,
  type ManagedTenant,
  type ManagedTenantIntegration,
  type WorkflowTemplate,
  type WorkflowTemplateConfigField,
  type WorkflowTemplateInput,
  type XeroMirrorAccount,
} from "@/services/api/workflows";
import {
  applyAccountPick,
  applyContactPick,
  buildInstantiatePayload,
  emptyFeeMapping,
  emptyPattern,
  feeMappingSources,
  initialFormState,
  prefilledFormState,
  multiselectValues,
  proposeShortName,
  proposeTenantSlug,
  configFieldConnector,
  defaultIntegration,
  initialIntegration,
  instanceWorkflowKey,
  orderedIntegrations,
  switchOnRefusal,
  toggleMultiselect,
  withIntegrationChange,
  workflowKeysForIntegration,
  withConfigDependencyChange,
  withFeeMappingDependencyChange,
  withInputChange,
  type FeeMappingRow,
  type TemplateFormState,
} from "./-templateForm";
import {
  groupTemplatesByProvider,
  providerSlug,
  showsProviderChips,
  visibleGroups,
} from "./-templateGroups";

export const Route = createFileRoute("/portal/workflows/templates")({
  component: WorkflowTemplatesPage,
});

const DEFINITIONS_PATH = "/portal/workflows/definitions";

// dp's patterns (api/v2/managed_tenants.py) — the server re-checks both.
const SHORT_NAME_RE = /^[a-z][a-z0-9]{1,7}$/;
const TENANT_RE = /^[a-z][a-z0-9-]{1,31}$/;

/** The pick-lists sit in the grid beside shadcn Inputs; give them the same look. */
const PICK_LIST_CLASS =
  "flex h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 shadow-sm disabled:cursor-not-allowed disabled:opacity-60";

/** A registration row with its org's integrations: one read path for the page load and
 *  the card's register / 409-recover paths. A registered org with no default integration
 *  is a broken registry, not something to switch templates on into. `row` is given when
 *  the caller already holds it (the register POST answers with it). */
async function readRegistration(
  orgId: string,
  token: string | null | undefined,
  row?: ManagedTenant,
): Promise<{ row: ManagedTenant; integrations: ManagedTenantIntegration[] }> {
  row ??= await workflowsApi.getManagedTenant(orgId, token);
  const integrations = await workflowsApi.listManagedTenantIntegrations(orgId, token);
  if (!defaultIntegration(integrations)) {
    throw new Error(`The managed-tenant registry has no default integration for ${row.tenant}.`);
  }
  return { row, integrations };
}

type Registration =
  | { status: "no-org" }
  | { status: "loading" }
  | { status: "registered"; row: ManagedTenant; integrations: ManagedTenantIntegration[] }
  | { status: "unregistered" }
  | { status: "error"; message: string };

/** How long the Switch on form waits for the existing instance's read before giving up. */
const PREFILL_TIMEOUT_MS = 30_000;

/** The Switch on form's read of the org's existing instance (web #1402). */
type Prefill =
  | { status: "loading" }
  | { status: "new" }
  | { status: "prefilled"; current: CurrentTemplateInstance; unread: string[] }
  | { status: "error"; message: string };

function WorkflowTemplatesPage() {
  const { isPlatformAdmin, rbacResolving } = usePermissions();

  // isPlatformAdmin reads false until the RBAC answer settles — hold off, or a platform
  // admin sees the no-access alert flash before the gallery.
  if (rbacResolving) {
    return null;
  }

  if (!isPlatformAdmin) {
    return (
      <>
        <Helmet>
          <title>Workflow templates | Portal</title>
        </Helmet>
        <div className="space-y-6">
          <PageHeader title="Workflow templates" icon={LayoutTemplate} eyebrow="Authoring" />
          <Alert>
            <ShieldOff className="h-4 w-4" />
            <AlertDescription>
              Workflow templates are switched on by Larasoft platform administrators. You
              don't have access to this area.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }
  return <TemplateGallery />;
}

function TemplateGallery() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const [templates, setTemplates] = useState<WorkflowTemplate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [registration, setRegistration] = useState<Registration>({ status: "loading" });
  const orgId = selectedOrganisation?.organisationId ?? null;
  // The token refreshes mid-session (AuthContext's token-refresh event). Re-reading the
  // registration on that would unmount an open form and lose what the operator typed, so
  // the effect follows the org only and reads the current token from a ref.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  // A registration POST still in flight when the org changes must not land on the next
  // org's page — the card is remounted, but this callback outlives it.
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  const acceptRegistration = (row: ManagedTenant, integrations: ManagedTenantIntegration[]) => {
    if (row.organisation_id.toLowerCase() === (orgIdRef.current ?? "").toLowerCase()) {
      setRegistration({ status: "registered", row, integrations });
    }
  };

  useEffect(() => {
    setActive(null);
    if (!orgId) {
      setRegistration({ status: "no-org" });
      return;
    }
    let cancelled = false;
    setRegistration({ status: "loading" });
    readRegistration(orgId, tokenRef.current)
      .then((r) => !cancelled && setRegistration({ status: "registered", ...r }))
      .catch((e: unknown) => {
        if (cancelled) return;
        // Only dp's own "not registered" 404 means unregistered; any other 404 (a
        // route that is not there) is an error, not an invitation to register.
        if (e instanceof PortalApiError && e.status === 404 && e.message.includes("not registered")) {
          setRegistration({ status: "unregistered" });
        }
        else setRegistration({ status: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const registered = registration.status === "registered" ? registration : null;

  // Cards under their provider heading, in catalogue order. The chip filter is offered only
  // once there are two providers; with one, a stale filter would hide nothing anyway.
  const [providerFilter, setProviderFilter] = useState<string | null>(null);
  const groups = templates ? groupTemplatesByProvider(templates) : null;
  const shownFilter =
    groups && showsProviderChips(groups) && groups.some((g) => g.provider === providerFilter)
      ? providerFilter
      : null;

  useEffect(() => {
    let cancelled = false;
    workflowsApi
      .listWorkflowTemplates(token)
      .then((t) => !cancelled && setTemplates(t))
      .catch((e: unknown) => !cancelled && setLoadError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <>
      <Helmet>
        <title>Workflow templates | Portal</title>
      </Helmet>
      <div className="space-y-6">
        <PageHeader
          title="Workflow templates"
          description="Switch a ready-made integration on for an organisation. It is created disabled — enable it from Workflow Definitions when you are ready."
          icon={LayoutTemplate}
          eyebrow="Authoring"
        />

        <RegistrationCard
          key={orgId ?? "none"}
          orgId={orgId}
          orgName={selectedOrganisation?.organisationName ?? null}
          registration={registration}
          token={token ?? undefined}
          onRegistered={acceptRegistration}
        />

        {loadError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}
        {!templates && !loadError && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        )}

        {groups && showsProviderChips(groups) && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by provider" data-testid="provider-chips">
            {[null, ...groups.map((g) => g.provider)].map((provider) => {
              const selected = shownFilter === provider;
              return (
                <Button
                  key={provider ?? "all"}
                  size="sm"
                  variant={selected ? "default" : "outline"}
                  className="rounded-full"
                  aria-pressed={selected}
                  data-testid={`provider-chip-${provider === null ? "all" : providerSlug(provider)}`}
                  onClick={() => setProviderFilter(provider)}
                >
                  {provider ?? "All"}
                </Button>
              );
            })}
          </div>
        )}

        {groups &&
          visibleGroups(groups, shownFilter).map((group) => (
            <section
              key={group.provider}
              className="space-y-4"
              aria-labelledby={`provider-${providerSlug(group.provider)}`}
              data-testid={`provider-group-${providerSlug(group.provider)}`}
            >
              <h2
                id={`provider-${providerSlug(group.provider)}`}
                className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {group.provider}
              </h2>
              {group.templates.map((template) => (
                <Card key={template.key} data-testid={`template-${template.key}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle>{template.name}</CardTitle>
                        <CardDescription className="mt-1">{template.description}</CardDescription>
                        <TemplatePrice templateKey={template.key} />
                      </div>
                      {active !== template.key && (
                        <Button onClick={() => setActive(template.key)} disabled={registered === null}>
                          <Power className="mr-2 h-4 w-4" />
                          Switch on
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  {active !== template.key && template.how_it_works && (
                    <CardContent>
                      <TemplateHowItWorks templateKey={template.key} howItWorks={template.how_it_works} />
                    </CardContent>
                  )}
                  {active === template.key && registered && (
                    <CardContent>
                      <SwitchOnForm
                        key={registered.row.organisation_id}
                        template={template}
                        registration={registered.row}
                        integrations={registered.integrations}
                        orgName={selectedOrganisation?.organisationName ?? null}
                        token={token ?? undefined}
                        onCancel={() => setActive(null)}
                      />
                    </CardContent>
                  )}
                </Card>
              ))}
            </section>
          ))}
      </div>
    </>
  );
}

function RegistrationCard({
  orgId,
  orgName,
  registration,
  token,
  onRegistered,
}: {
  orgId: string | null;
  orgName: string | null;
  registration: Registration;
  token: string | undefined;
  onRegistered: (row: ManagedTenant, integrations: ManagedTenantIntegration[]) => void;
}) {
  const [shortName, setShortName] = useState(() => proposeShortName(orgName ?? ""));
  const [tenant, setTenant] = useState(() => proposeTenantSlug(orgName ?? ""));
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const register = async () => {
    if (!orgId) return;
    const problems: string[] = [];
    if (!SHORT_NAME_RE.test(shortName)) {
      problems.push("Short name: 2–8 lower-case letters or digits, starting with a letter");
    }
    if (!TENANT_RE.test(tenant)) {
      problems.push("Tenant folder: 2–32 lower-case letters, digits or hyphens, starting with a letter");
    }
    if (problems.length > 0) {
      setErrors(problems);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      const row = await workflowsApi.registerManagedTenant({ org_id: orgId, short_name: shortName, tenant }, token);
      const r = await readRegistration(orgId, token, row);
      onRegistered(r.row, r.integrations);
    } catch (e) {
      // Someone registered this org first: show their row rather than a dead form.
      if (e instanceof PortalApiError && e.status === 409) {
        try {
          const r = await readRegistration(orgId, token);
          onRegistered(r.row, r.integrations);
          return;
        } catch {
          // fall through to the conflict message
        }
      }
      setErrors([e instanceof Error ? e.message : "Registering failed"]);
    } finally {
      setSaving(false);
    }
  };

  let body: ReactNode;
  switch (registration.status) {
    case "no-org":
      body = <p className="text-sm text-muted-foreground">Select an organisation first.</p>;
      break;
    case "loading":
      body = (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking the registration…
        </div>
      );
      break;
    case "error":
      body = (
        <Alert variant="destructive" data-testid="registration-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{registration.message}</AlertDescription>
        </Alert>
      );
      break;
    case "registered":
      body =
        registration.integrations.length > 1 ? (
          <IntegrationList row={registration.row} integrations={registration.integrations} />
        ) : (
          <p className="text-sm" data-testid="registration-registered">
            Tenant <code>{registration.row.tenant}</code> · short name <code>{registration.row.short_name}</code> ·
            control schema <code>{registration.row.control_schema}</code>. Every template switched on for this
            organisation lands in that schema.
          </p>
        );
      break;
    case "unregistered":
      body = (
        <div className="space-y-4" data-testid="registration-form">
          <p className="text-sm text-muted-foreground">
            This organisation is not a managed tenant yet. Register it to switch templates on — its workflows then
            become canonical (exported, drift-checked, restorable). Nothing is created on its database until the
            first switch-on.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="reg-short_name">Short name</Label>
              <Input
                id="reg-short_name"
                value={shortName}
                onChange={(e) => setShortName(e.target.value.trim().toLowerCase())}
                placeholder="sn"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The control schema will be <code>{shortName || "<short>"}-control</code>. It cannot be changed later.
              </p>
            </div>
            <div>
              <Label htmlFor="reg-tenant">Tenant folder</Label>
              <Input
                id="reg-tenant"
                value={tenant}
                onChange={(e) => setTenant(e.target.value.trim().toLowerCase())}
                placeholder="supernova"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The folder under <code>schemas/tenants/</code> that holds this organisation's canonical workflows,
                views and tables.
              </p>
            </div>
          </div>
          {errors.length > 0 && (
            <Alert variant="destructive" data-testid="registration-errors">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </AlertDescription>
            </Alert>
          )}
          <Button onClick={() => void register()} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Building2 className="mr-2 h-4 w-4" />}
            Register {orgName ?? "organisation"}
          </Button>
        </div>
      );
      break;
  }

  return (
    <Card data-testid="registration-card">
      <CardHeader>
        <CardTitle>Customer registration</CardTitle>
        <CardDescription className="mt-1">{orgName ?? "No organisation selected"}</CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

/** The registration card's body for an org with more than one integration (D17): one row
 *  per integration, default first, each naming the schema and folder it owns. */
function IntegrationList({ row, integrations }: { row: ManagedTenant; integrations: ManagedTenantIntegration[] }) {
  const ordered = orderedIntegrations(integrations);
  return (
    <div className="space-y-3" data-testid="registration-registered">
      <p className="text-sm">
        Tenant <code>{row.tenant}</code> · short name <code>{row.short_name}</code> · {integrations.length}{" "}
        integrations. Each template is switched on into one integration's control schema.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="registration-integrations">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-4 font-medium">Integration</th>
              <th className="py-1 pr-4 font-medium">Key</th>
              <th className="py-1 pr-4 font-medium">Control schema</th>
              <th className="py-1 font-medium">Canonical folder</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((i) => (
              <tr key={i.integration_key} className="border-t" data-testid={`integration-${i.integration_key}`}>
                <td className="py-2 pr-4">
                  {i.display_name}
                  {i.is_default && (
                    <Badge variant="secondary" className="ml-2">
                      Default
                    </Badge>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <code>{i.integration_key}</code>
                </td>
                <td className="py-2 pr-4">
                  <code>{i.control_schema}</code>
                </td>
                <td className="py-2">
                  <code>{i.canonical_folder}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SwitchOnForm({
  template,
  registration,
  integrations,
  orgName,
  token,
  onCancel,
}: {
  template: WorkflowTemplate;
  registration: ManagedTenant;
  integrations: ManagedTenantIntegration[];
  orgName: string | null;
  token: string | undefined;
  onCancel: () => void;
}) {
  const [state, setState] = useState<TemplateFormState>(() => initialFormState(template));
  // The integration the instance goes into. readRegistration guarantees a default.
  const [integrationKey, setIntegrationKey] = useState(
    () => (initialIntegration(template, integrations) as ManagedTenantIntegration).integration_key,
  );
  const integration =
    integrations.find((i) => i.integration_key === integrationKey) ??
    (initialIntegration(template, integrations) as ManagedTenantIntegration);
  const choosesIntegration = integrations.length > 1;
  const instanceKey = instanceWorkflowKey(template.workflow_key, integration);
  const pickIntegration = (key: string) => {
    if (key === integrationKey) return;
    setIntegrationKey(key);
    setState((s) => withIntegrationChange(template, s));
  };
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<InstantiateWorkflowTemplateResponse | null>(null);

  // The org's existing instance in this integration, if any (web #1402): the form opens on
  // the inputs it was switched on with, so a re-switch-on to pick up a fixed template build
  // re-renders the same settings unless the operator changes one. Read per integration —
  // each has its own instance — and before the form is shown, so a late answer never
  // overwrites what the operator typed.
  const [prefill, setPrefill] = useState<Prefill>({ status: "loading" });
  const prefillIntegration = choosesIntegration ? integration.integration_key : undefined;
  // Whether the form currently holds another integration's instance values: an integration
  // with no instance must not inherit them unannounced, so it starts blank instead.
  const holdsPrefill = useRef(false);
  useEffect(() => {
    let cancelled = false;
    // Aborted on cleanup (an integration switch, Cancel) so a read nobody waits for stops, and
    // after PREFILL_TIMEOUT_MS so a read that never answers ends in the error notice.
    const abort = new AbortController();
    const timer = setTimeout(
      () => abort.abort(new DOMException("the read timed out", "TimeoutError")),
      PREFILL_TIMEOUT_MS,
    );
    setPrefill({ status: "loading" });
    const startBlank = () => {
      if (holdsPrefill.current) setState(initialFormState(template));
      holdsPrefill.current = false;
    };
    workflowsApi
      .getCurrentTemplateInstance(
        template.key,
        registration.organisation_id,
        prefillIntegration,
        token,
        // A read that never answers must not hold the form forever: it ends in the error notice.
        abort.signal,
      )
      .then((current) => {
        if (cancelled) return;
        const { state: next, unread } = prefilledFormState(template, current);
        setState(next);
        holdsPrefill.current = true;
        setPrefill({ status: "prefilled", current, unread });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        startBlank();
        // Only dp's own answer means "no instance". Any other 404 (a dp without the route) is a
        // failed read, so the blank form is announced rather than passed off as a first switch-on.
        if (e instanceof PortalApiError && e.status === 404 && e.message.includes("has no instance")) {
          setPrefill({ status: "new" });
        } else if (e instanceof DOMException && e.name === "TimeoutError") {
          setPrefill({ status: "error", message: "the read timed out" });
        } else {
          setPrefill({ status: "error", message: e instanceof Error ? e.message : String(e) });
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      abort.abort();
    };
    // Once per integration: the token refreshing mid-session is not a reason to re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.key, registration.organisation_id, prefillIntegration]);

  // Picked GL accounts' labels by id, so a row shows "200 — Sales (REVENUE)" for what it
  // holds. An id's label is a fact of the mirror, not of the row.
  const [accountLabels, setAccountLabels] = useState<Record<string, string>>({});
  // A picked account's "enter the batch prefix" warning, by id for the same reason: rows are
  // keyed by position, so state held in a row would follow a row removal onto its neighbour.
  const [codeWarnings, setCodeWarnings] = useState<Record<string, string>>({});
  // Picked tax rates' names by "<mirror connector>|<TaxType>", so the field shows
  // "Standard Rate (OUTPUT3)" — and never one mirror's name for the same code under another.
  const [taxRateNames, setTaxRateNames] = useState<Record<string, string>>({});
  const mirror = feeMappingSources(template);
  const inputLabel = (name: string | null | undefined) => template.inputs.find((i) => i.name === name)?.label ?? "";
  // The mirror connector a tax-rate field is picked from; "" until chosen (the picker waits).
  const taxRateConnector = (field: WorkflowTemplateConfigField) => {
    const connector = configFieldConnector(template, field);
    return connector ? (state.inputs[connector] ?? "") : "";
  };

  const setInput = (name: string, value: string) =>
    setState((s) => {
      const inputs = withInputChange(template, s.inputs, name, value);
      return {
        ...s,
        inputs,
        config: withConfigDependencyChange(template, s.inputs, inputs, s.config),
        feeMappings: withFeeMappingDependencyChange(template, s.inputs, inputs, s.feeMappings),
      };
    });
  const setConfig = (name: string, value: string) =>
    setState((s) => ({ ...s, config: { ...s.config, [name]: value } }));
  // The org's workflows, for a `workflow_key` field (the Start field: start after
  // another workflow instead of on the source sync). Loaded only when one is declared.
  const needsWorkflowKeys = Object.values(template.config_schema).some((f) => f.type === "workflow_key");
  const [workflowKeys, setWorkflowKeys] = useState<DefinitionKeyResponse[] | null>(null);
  useEffect(() => {
    if (!needsWorkflowKeys) return;
    let cancelled = false;
    workflowsApi
      .listDefinitionKeys(token)
      .then((rows) => !cancelled && setWorkflowKeys(Array.isArray(rows) ? rows : []))
      .catch(() => !cancelled && setWorkflowKeys([]));
    return () => {
      cancelled = true;
    };
    // Once per form: the token refreshing mid-session is not a reason to re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsWorkflowKeys]);
  const setMapping = (i: number, patch: Partial<FeeMappingRow>) =>
    setState((s) => ({
      ...s,
      feeMappings: s.feeMappings.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    }));

  const submit = async () => {
    const built = buildInstantiatePayload(
      template,
      state,
      registration.organisation_id,
      choosesIntegration ? integration.integration_key : undefined,
    );
    if (built.payload === null) {
      setErrors(built.errors);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      setResult(await workflowsApi.instantiateWorkflowTemplate(template.key, built.payload, token));
    } catch (e) {
      setErrors([e instanceof Error ? e.message : "Switching on failed"]);
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <Alert data-testid="template-switched-on">
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription className="space-y-2">
          <p>
            <strong>{result.workflow_key}</strong> is switched on for {orgName ?? "this organisation"} —{" "}
            {/* The state word is the instance's real IsEnabled, never assumed: a re-run on an
                enabled instance is reused as-is and stays enabled (dp #2387). */}
            {result.definition.outcome === "replaced"
              ? `replaced with these inputs as version ${result.definition.version},${result.definition.is_enabled ? "" : " still"}`
              : result.definition.outcome === "reused"
                ? `already there,${result.definition.is_enabled ? "" : " still"}`
                : "created"}{" "}
            <strong data-testid="template-enabled-state">
              {result.definition.is_enabled ? "enabled" : "disabled"}
            </strong>
            . Tables {result.tables_created.join(", ") || "already present"}; views{" "}
            {result.views_applied.join(", ") || "already present"} in <code>{result.control_schema}</code>
            <span data-testid="template-control-schema">
              {result.control_schema_created
                ? ", which this switch-on created on the customer's database"
                : ", which was already there"}
            </span>
            .
          </p>
          {(result.blocked.length > 0 || !result.post_sweep_ok) && (
            <p data-testid="template-blocked" className="font-medium text-destructive">
              Not everything applied cleanly
              {result.blocked.length > 0 ? `: ${result.blocked.join(", ")} blocked` : ""}. Check the schema
              drift page for <code>{result.control_schema}</code>
              {result.definition.is_enabled ? " — the instance is already enabled." : " before enabling."}
            </p>
          )}
          {result.superseded_schemas.length > 0 && (
            <p data-testid="template-superseded">
              The previous schema {result.superseded_schemas.join(", ")} still holds this instance's old tables
              and views. Remove them by hand once you no longer need them.
            </p>
          )}
          <p>
            <Link
              to={DEFINITIONS_PATH}
              className="font-medium underline underline-offset-4"
              data-testid="template-definition-link"
            >
              Open {result.workflow_key} in Workflow Definitions
            </Link>{" "}
            {result.definition.is_enabled ? "to review its config." : "to review its config and enable it."}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  const configFields = Object.entries(template.config_schema).filter(([, f]) => f.type !== "fee_mappings");
  const mappingField = Object.entries(template.config_schema).find(([, f]) => f.type === "fee_mappings");

  if (prefill.status === "loading") {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="template-prefill-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reading this organisation&apos;s current {template.name} instance…
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="template-prefill-cancel">
          Cancel
        </Button>
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="template-form">
      <TemplateHowItWorks templateKey={template.key} howItWorks={template.how_it_works} defaultOpen />
      {prefill.status === "prefilled" && (
        <Alert data-testid="template-prefilled">
          <AlertDescription className="space-y-1">
            <p>
              Filled in from the current instance, <code>{prefill.current.workflow_key}</code> version{" "}
              {prefill.current.version} ({prefill.current.is_enabled ? "enabled" : "disabled"}). Submitted unchanged,
              it is switched on again with the same settings on the latest template build.
            </p>
            {prefill.unread.length > 0 && (
              <p data-testid="template-prefill-unread" className="flex items-start gap-1.5 font-medium text-amber-700">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Not read from the instance, fill in: {prefill.unread.join(", ")}.</span>
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}
      {prefill.status === "error" && (
        <Alert variant="destructive" data-testid="template-prefill-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Could not read the current instance&apos;s settings ({prefill.message}). The form starts blank: enter
            every field as it should be.
          </AlertDescription>
        </Alert>
      )}
      {choosesIntegration && (
        <div className="max-w-md">
          <Label htmlFor="template-integration">Integration</Label>
          <Select value={integrationKey} onValueChange={pickIntegration}>
            <SelectTrigger id="template-integration" data-testid="template-integration">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orderedIntegrations(integrations).map((i) => {
                const refusal = switchOnRefusal(template, i);
                return (
                  <SelectItem key={i.integration_key} value={i.integration_key} disabled={refusal !== null}>
                    {i.display_name} ({i.integration_key}){i.is_default ? " — default" : ""}
                    {refusal ? ` — ${refusal}` : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="template-instance-key">
            This instance is keyed <code>{instanceKey}</code>.
          </p>
        </div>
      )}
      <p className="text-sm text-muted-foreground" data-testid="template-control-schema-line">
        Switching on for <strong>{orgName ?? "this organisation"}</strong>. Control schema:{" "}
        <code>{integration.control_schema}</code> (created if absent).
      </p>

      <section className="grid gap-4 md:grid-cols-2">
        {template.inputs.map((input) => (
          <div key={input.name}>
            <Label htmlFor={`in-${input.name}`}>{input.label}</Label>
            <TemplateInputControl
              template={template}
              controlSchema={integration.control_schema}
              input={input}
              inputs={state.inputs}
              onChange={(value) => setInput(input.name, value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">{input.description}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {configFields.map(([key, field]) => (
          <div key={key}>
            <Label id={`cfg-${key}-label`} htmlFor={field.type === "multiselect" ? undefined : `cfg-${key}`}>
              {field.label}
            </Label>
            {field.type === "multiselect" ? (
              <MultiselectField
                id={`cfg-${key}`}
                field={field}
                value={state.config[key] ?? ""}
                onChange={(v) => setConfig(key, v)}
              />
            ) : field.type === "xero_tax_rate" ? (
              <XeroMirrorCombobox
                key={`${taxRateConnector(field)}|${state.config[key] ?? ""}`}
                id={`cfg-${key}`}
                mode="tax-rates"
                connectorKey={taxRateConnector(field)}
                value={state.config[key] ?? ""}
                valueLabel={taxRateNames[`${taxRateConnector(field)}|${state.config[key] ?? ""}`]}
                waitingFor={inputLabel(configFieldConnector(template, field))}
                ariaLabel={field.label}
                onPick={(rate) => {
                  if (!rate) return;
                  setTaxRateNames((n) => ({ ...n, [`${taxRateConnector(field)}|${rate.tax_type}`]: rate.name }));
                  setConfig(key, rate.tax_type);
                }}
              />
            ) : field.type === "workflow_key" ? (
              <WorkflowKeySelect
                id={`cfg-${key}`}
                value={state.config[key] ?? ""}
                options={workflowKeysForIntegration(workflowKeys, integration, integrations)}
                ownKey={instanceKey}
                onChange={(v) => setConfig(key, v)}
              />
            ) : field.type === "enum" ? (
              <Select value={state.config[key] ?? ""} onValueChange={(v) => setConfig(key, v)}>
                <SelectTrigger id={`cfg-${key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(field.enum ?? []).map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`cfg-${key}`}
                type={field.type === "date" ? "date" : field.type === "integer" ? "number" : "text"}
                min={field.minimum}
                max={field.maximum}
                value={state.config[key] ?? ""}
                onChange={(e) => setConfig(key, e.target.value)}
              />
            )}
            {field.description && <p className="mt-1 text-xs text-muted-foreground">{field.description}</p>}
          </div>
        ))}
      </section>

      {mappingField && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{mappingField[1].label}</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setState((s) => ({ ...s, feeMappings: [...s.feeMappings, emptyFeeMapping()] }))}
            >
              <Plus className="mr-1 h-4 w-4" /> Add mapping
            </Button>
          </div>
          {state.feeMappings.map((m, i) => (
            <FeeMappingEditor
              key={i}
              index={i}
              mapping={m}
              connectorKey={mirror ? (state.inputs[mirror.connector] ?? "") : ""}
              waitingFor={inputLabel(mirror?.connector)}
              accountLabel={accountLabels[m.gl_account_id]}
              codeWarning={codeWarnings[m.gl_account_id]}
              onAccountPicked={(a, warning) => {
                setAccountLabels((l) => ({ ...l, [a.id]: xeroMirrorLabel(a) }));
                setCodeWarnings((w) => {
                  const next = { ...w };
                  if (warning) next[a.id] = warning;
                  else delete next[a.id];
                  return next;
                });
              }}
              onChange={(patch) => setMapping(i, patch)}
              onRemove={
                state.feeMappings.length > 1
                  ? () => setState((s) => ({ ...s, feeMappings: s.feeMappings.filter((_, j) => j !== i) }))
                  : undefined
              }
            />
          ))}
        </section>
      )}

      {errors.length > 0 && (
        <Alert variant="destructive" data-testid="template-errors">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        <Button onClick={() => void submit()} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Power className="mr-2 h-4 w-4" />}
          Switch on (disabled)
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** One template input, drawn by its kind: connectors from the org's B2C connectors, a
 *  `sql_name` from the views in the org's control schema on the database of the
 *  connector it depends on. An unknown kind (a newer dp), or a `sql_name` whose
 *  `depends_on` is not a connector input, is a plain text box. */
function TemplateInputControl({
  template,
  controlSchema,
  input,
  inputs,
  onChange,
}: {
  template: WorkflowTemplate;
  controlSchema: string;
  input: WorkflowTemplateInput;
  inputs: Record<string, string>;
  onChange: (value: string) => void;
}) {
  const id = `in-${input.name}`;
  const value = inputs[input.name] ?? "";
  const parent = template.inputs.find((i) => i.name === input.depends_on);

  if (input.kind === "connector_key") {
    return (
      <PickerField
        id={id}
        className={PICK_LIST_CLASS}
        refType="b2c.connector"
        value={value}
        onChange={onChange}
        emptyState={{ placeholder: "No connectors", hint: "This organisation has no B2C connectors." }}
      />
    );
  }
  if (input.kind === "sql_name" && parent?.kind === "connector_key") {
    return (
      <ConnectorObjectSelect
        id={id}
        className={PICK_LIST_CLASS}
        connectorKey={inputs[parent.name] ?? ""}
        schema={controlSchema}
        value={value}
        onChange={onChange}
        waitingFor={parent.label}
      />
    );
  }
  return (
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={input.kind === "date" ? "date" : input.kind === "integer" ? "number" : "text"}
      placeholder={input.kind === "text" ? "%" : ""}
    />
  );
}

/** A `multiselect` config field: one checkbox per option, ticked from the field's
 *  comma-separated value (the template default to start). Nothing ticked is allowed — dp
 *  accepts an empty list and gives it a meaning (the field's description says which). */
function MultiselectField({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: WorkflowTemplateConfigField;
  value: string;
  onChange: (value: string) => void;
}) {
  const ticked = multiselectValues(field, value);
  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`} className="mt-1 space-y-2">
      {(field.options ?? []).map((option) => (
        <label key={option} className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={ticked.includes(option)}
            onCheckedChange={(v) => onChange(toggleMultiselect(field, value, option, v === true))}
            aria-label={option}
          />
          {option}
        </label>
      ))}
      {ticked.length === 0 && (
        <p role="status" className="text-xs font-medium text-amber-700" data-testid={`${id}-none`}>
          None selected.
        </p>
      )}
    </div>
  );
}

function FeeMappingEditor({
  index,
  mapping,
  connectorKey,
  waitingFor,
  accountLabel,
  codeWarning,
  onAccountPicked,
  onChange,
  onRemove,
}: {
  index: number;
  mapping: FeeMappingRow;
  /** The ledger mirror connector the pickers read; "" until chosen (the pickers wait). */
  connectorKey: string;
  waitingFor: string;
  accountLabel?: string;
  /** The held account's "enter the batch prefix" warning, if its Code could not be used. */
  codeWarning?: string;
  onAccountPicked: (account: XeroMirrorAccount, warning: string | null) => void;
  onChange: (patch: Partial<FeeMappingRow>) => void;
  onRemove?: () => void;
}) {
  const n = index + 1;
  // A picked id, read-only: it comes from the mirror, never typed.
  const pickedId = (key: "contact_id" | "gl_account_id", label: string) => (
    <div>
      <Label htmlFor={`fm-${index}-${key}`}>{label}</Label>
      <Input id={`fm-${index}-${key}`} value={mapping[key]} readOnly placeholder="Picked above" />
    </div>
  );
  const text = (key: keyof FeeMappingRow, label: string, placeholder = "", type = "text") => (
    <div>
      <Label htmlFor={`fm-${index}-${key}`}>{label}</Label>
      <Input
        id={`fm-${index}-${key}`}
        type={type}
        value={mapping[key] as string}
        placeholder={placeholder}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<FeeMappingRow>)}
      />
    </div>
  );
  const setPattern = (j: number, patch: Partial<{ pattern: string; label: string }>) =>
    onChange({
      detail_patterns: mapping.detail_patterns.map((p, k) => (k === j ? { ...p, ...patch } : p)),
    });

  return (
    <div className="space-y-3 rounded-lg border p-4" data-testid={`fee-mapping-${index}`}>
      <div className="flex items-center justify-between">
        <Badge variant="secondary">Mapping {n}</Badge>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={mapping.enabled}
              onCheckedChange={(v) => onChange({ enabled: v === true })}
              aria-label={`Mapping ${n} enabled`}
            />
            Enabled
          </label>
          {onRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove mapping ${n}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label>Contact</Label>
          <XeroMirrorCombobox
            key={`${connectorKey}|${mapping.contact_id}`}
            id={`fm-${index}-contact`}
            mode="contacts"
            connectorKey={connectorKey}
            value={mapping.contact_id}
            valueLabel={mapping.contact_name || mapping.contact_id}
            waitingFor={waitingFor}
            ariaLabel={`Mapping ${n} contact`}
            onPick={(c) => onChange(applyContactPick(c))}
          />
        </div>
        <div>
          <Label>GL account</Label>
          <XeroMirrorCombobox
            key={`${connectorKey}|${mapping.gl_account_id}`}
            id={`fm-${index}-account`}
            mode="accounts"
            connectorKey={connectorKey}
            value={mapping.gl_account_id}
            valueLabel={accountLabel ?? mapping.gl_account_id}
            waitingFor={waitingFor}
            ariaLabel={`Mapping ${n} GL account`}
            onPick={(a) => {
              const { patch, warning } = applyAccountPick(a);
              if (a) onAccountPicked(a, warning);
              onChange(patch);
            }}
          />
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {text("contact_name", "Customer name on the credit note", "Picked with the contact")}
        {pickedId("contact_id", "Xero contact id")}
        <div>
          {text("account_code", "Batch prefix (GL account code)", "Defaults to the account's code")}
          {codeWarning && (
            <p className="mt-1 text-xs text-destructive" data-testid={`fm-${index}-code-warning`}>
              {codeWarning}
            </p>
          )}
        </div>
        {pickedId("gl_account_id", "Xero GL account id")}
        {text("go_live_date", "Go-live date", "", "date")}
      </div>
      <div className="space-y-2">
        <Label>Fee lines (first matching pattern names the credit-note line)</Label>
        {mapping.detail_patterns.map((p, j) => (
          <div key={j} className="flex gap-2">
            <Input
              aria-label={`Mapping ${n} pattern ${j + 1}`}
              value={p.pattern}
              placeholder="%Commission Fee%"
              onChange={(e) => setPattern(j, { pattern: e.target.value })}
            />
            <Input
              aria-label={`Mapping ${n} label ${j + 1}`}
              value={p.label}
              placeholder="Commission fee"
              onChange={(e) => setPattern(j, { label: e.target.value })}
            />
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({ detail_patterns: [...mapping.detail_patterns, emptyPattern()] })}
        >
          <Plus className="mr-1 h-4 w-4" /> Add pattern
        </Button>
      </div>
    </div>
  );
}
